<template>
  <div
    class="bg-background-neutral-secondary w-full space-y-4 overflow-x-hidden rounded-2xl p-4"
  >
    <div
      v-if="isLoading"
      class="flex flex-col items-center justify-center space-y-4"
    >
      <div class="shimmer h-[66px] w-full rounded-2xl"></div>
      <div class="shimmer h-10 w-full rounded-2xl"></div>
    </div>
    <template v-else>
      <div class="flex items-center space-x-2">
        <img
          src="@/assets/images/seat-map-icon.webp"
          width="48"
          height="48"
          alt=""
        />
        <div class="w-full space-y-0.5 text-left">
          <h2
            class="text-text-neutral-primary flex justify-between text-base font-semibold"
          >
            <span>
              {{ t('ancillaries.seatMap.title') }}
            </span>
            <span v-if="seatMapStateBySegment">{{ totalPrice }}</span>
          </h2>
          <div v-if="seatMapStateBySegment">
            <p
              v-for="(_, idx) in flightsSegments"
              :key="idx"
              class="text-text-neutral-secondary text-sm"
            >
              {{ getSegmentSeatsText(idx) }}
            </p>
          </div>
          <p v-else class="text-text-neutral-secondary text-sm">
            {{ t('ancillaries.seatMap.description') }}
          </p>
        </div>
      </div>
      <FreeButton
        size="sm"
        type="button"
        class="w-full"
        :class="seatMapStateBySegment ? 'gray-button' : 'green-main-button'"
        @click="emit('onProceedToSeatMap')"
      >
        {{
          seatMapStateBySegment
            ? t('ancillaries.seatMap.changeSeats')
            : t('ancillaries.seatMap.selectSeats')
        }}
      </FreeButton>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { prettyPrice } from '@aviata/text-fns';
  import { FreeButton } from 'freedom-shared';
  import type { PassengerKey, PassengersData } from '@aviata/booking-data';
  import type { flightEntity } from 'freedom-shared';

  import { seatMapLib } from '@/widgets/seat-map';
  import type { SelectedSeatsState } from '@/widgets/seat-map';

  const props = defineProps<{
    offerId: string;
    isLoading: boolean;
    seatMapStateBySegment: SelectedSeatsState | null;
    mappedFlights: flightEntity.types.MappedFlight[];
    passengersData: PassengersData;
  }>();

  const emit = defineEmits<{
    onProceedToSeatMap: [];
  }>();

  const { t } = useI18n();
  const passengersKeysWithoutInfants = computed(() => {
    const passengersKeys = Object.keys(props.passengersData) as PassengerKey[];
    return passengersKeys.filter(key => !seatMapLib.isInfant(key));
  });
  const flightsSegments = computed(() =>
    props.mappedFlights.flatMap(flight => flight.segments),
  );

  const totalPrice = computed(() => {
    if (!props.seatMapStateBySegment) {
      return 0;
    }
    const price = seatMapLib.getSeatMapPrice(
      props.seatMapStateBySegment,
      passengersKeysWithoutInfants.value,
    );
    if (price > 0) {
      return `${prettyPrice(price.toString())} ₸`;
    }
    return t('general.price.free');
  });

  function getSegmentSeatsText(segmentIdx: number) {
    if (!props.seatMapStateBySegment) {
      return '';
    }

    const { dep, arr } = flightsSegments.value[segmentIdx];
    const listOfSeats = passengersKeysWithoutInfants.value
      .filter(key =>
        seatMapLib.hasPassengerSeat(
          props.seatMapStateBySegment[segmentIdx],
          key,
        ),
      )
      .map(key => props.seatMapStateBySegment[segmentIdx][key].seatKey)
      .join(', ');

    return listOfSeats
      ? `${dep.airport} — ${arr.airport} · ${listOfSeats}`
      : '';
  }
</script>
